import { b2AABB } from "./collision.js";
import { b2Math, b2Sweep, b2Vec2, b2XForm } from "./common/math.js";
import { b2Scalar } from "./common/precision.js";
import { b2Settings } from "./common/settings.js";
import { b2BodyDef, b2ContactFilter } from "./dynamics_core.js";
import { b2ContactManager, b2Island } from "./dynamics_engine.js";
import { b2Joint } from "./dynamics_joints.js";
import { b2FilterData, b2MassData, b2Shape } from "./shapes.js";

function createDefaultWorldAABB() {
  const aabb = new b2AABB();
  aabb.lowerBound.Set(-1000, -1000);
  aabb.upperBound.Set(1000, 1000);
  return aabb;
}

// ---------- AS3 b2BroadPhase helper classes ----------

class b2Bound {
  constructor() {
    this.value = 0;
    this.proxyId = 0;
    this.stabbingCount = 0;
  }

  IsLower() { return (this.value & 1) === 0; }
  IsUpper() { return (this.value & 1) === 1; }

  Swap(other) {
    const v = this.value; this.value = other.value; other.value = v;
    const p = this.proxyId; this.proxyId = other.proxyId; other.proxyId = p;
    const s = this.stabbingCount; this.stabbingCount = other.stabbingCount; other.stabbingCount = s;
  }
}

class b2BoundValues {
  constructor() {
    this.lowerValues = [0, 0];
    this.upperValues = [0, 0];
  }
}

class b2Proxy {
  constructor() {
    this.lowerBounds = [0, 0];
    this.upperBounds = [0, 0];
    this.overlapCount = 0;
    this.timeStamp = 0;
    this.userData = null;
    this._next = 0;
  }

  GetNext() { return this._next; }
  SetNext(n) { this._next = n; }
  IsValid() { return this.overlapCount !== b2BroadPhase.b2_invalid; }
}

class b2Pair {
  static b2_pairBuffered = 0x0001;
  static b2_pairRemoved = 0x0002;
  static b2_pairFinal = 0x0004;
  static b2_nullProxy = 0xffff;
  static b2_nullPair = 0xffff;
  // AS3: b2Pair.b2_tableCapacity = b2Settings.b2_maxPairs (= 8 * b2_maxProxies = 4096)
  static b2_tableCapacity = 8 * 512;
  static b2_tableMask = b2Pair.b2_tableCapacity - 1;

  constructor() {
    this.proxyId1 = b2Pair.b2_nullProxy;
    this.proxyId2 = b2Pair.b2_nullProxy;
    this.userData = null;
    this.status = 0;
    this.next = b2Pair.b2_nullPair;
  }

  SetBuffered() { this.status |= b2Pair.b2_pairBuffered; }
  ClearBuffered() { this.status &= ~b2Pair.b2_pairBuffered; }
  IsBuffered() { return (this.status & b2Pair.b2_pairBuffered) !== 0; }
  SetRemoved() { this.status |= b2Pair.b2_pairRemoved; }
  ClearRemoved() { this.status &= ~b2Pair.b2_pairRemoved; }
  IsRemoved() { return (this.status & b2Pair.b2_pairRemoved) !== 0; }
  SetFinal() { this.status |= b2Pair.b2_pairFinal; }
  IsFinal() { return (this.status & b2Pair.b2_pairFinal) !== 0; }
}

class b2BufferedPair {
  constructor() {
    this.proxyId1 = b2Pair.b2_nullProxy;
    this.proxyId2 = b2Pair.b2_nullProxy;
  }
}

// b2PairCallback — abstract base, overridden by b2ContactManager
class b2PairCallback {
  PairAdded(_userData1, _userData2) { return null; }
  PairRemoved(_userData1, _userData2, _pairData) { }
}

class b2PairManager {
  constructor() {
    this.m_broadPhase = null;
    this.m_callback = null;
    this.m_hashTable = new Array(b2Pair.b2_tableCapacity).fill(b2Pair.b2_nullPair);
    // AS3 uses b2Settings.b2_maxPairs for pool size
    const maxPairs = 8 * 512; // b2_maxPairs = 8 * b2_maxProxies
    this.m_pairs = new Array(maxPairs);
    this.m_pairBuffer = new Array(maxPairs);
    for (let i = 0; i < maxPairs; i++) {
      this.m_pairs[i] = new b2Pair();
      this.m_pairs[i].next = i + 1;
      this.m_pairBuffer[i] = new b2BufferedPair();
    }
    this.m_pairs[maxPairs - 1].next = b2Pair.b2_nullPair;
    this.m_pairCount = 0;
    this.m_pairBufferCount = 0;
    this.m_freePair = 0;
  }

  Initialize(broadPhase, callback) {
    this.m_broadPhase = broadPhase;
    this.m_callback = callback;
  }

  static Hash(id1, id2) {
    let h = ((id2 << 16) & 0xFFFF0000) | (id1 & 0xFFFF);
    h = (~h + ((h << 15) & 0xFFFF8000)) >>> 0;
    h = (h ^ ((h >> 12) & 0x000FFFFF)) >>> 0;
    h = (h + ((h << 2) & 0xFFFFFFFC)) >>> 0;
    h = (h ^ ((h >> 4) & 0x0FFFFFFF)) >>> 0;
    h = (Math.imul(h, 2057)) >>> 0;
    h = (h ^ ((h >> 16) & 0x0000FFFF)) >>> 0;
    return h;
  }

  static Equals(pair, id1, id2) {
    return pair.proxyId1 === id1 && pair.proxyId2 === id2;
  }

  _findHash(id1, id2, hash) {
    let index = this.m_hashTable[hash];
    while (index !== b2Pair.b2_nullPair && !b2PairManager.Equals(this.m_pairs[index], id1, id2)) {
      index = this.m_pairs[index].next;
    }
    return index === b2Pair.b2_nullPair ? null : this.m_pairs[index];
  }

  _find(id1, id2) {
    if (id1 > id2) { const t = id1; id1 = id2; id2 = t; }
    const hash = b2PairManager.Hash(id1, id2) & b2Pair.b2_tableMask;
    return this._findHash(id1, id2, hash);
  }

  _addPair(id1, id2) {
    if (id1 > id2) { const t = id1; id1 = id2; id2 = t; }
    const hash = b2PairManager.Hash(id1, id2) & b2Pair.b2_tableMask;
    let pair = this._findHash(id1, id2, hash);
    if (pair !== null) { return pair; }
    const pairIndex = this.m_freePair;
    pair = this.m_pairs[pairIndex];
    this.m_freePair = pair.next;
    pair.proxyId1 = id1;
    pair.proxyId2 = id2;
    pair.status = 0;
    pair.userData = null;
    pair.next = this.m_hashTable[hash];
    this.m_hashTable[hash] = pairIndex;
    this.m_pairCount += 1;
    return pair;
  }

  _removePair(id1, id2) {
    if (id1 > id2) { const t = id1; id1 = id2; id2 = t; }
    const hash = b2PairManager.Hash(id1, id2) & b2Pair.b2_tableMask;
    let index = this.m_hashTable[hash];
    let prevIndex = b2Pair.b2_nullPair;
    while (index !== b2Pair.b2_nullPair) {
      if (b2PairManager.Equals(this.m_pairs[index], id1, id2)) {
        if (prevIndex !== b2Pair.b2_nullPair) {
          this.m_pairs[prevIndex].next = this.m_pairs[index].next;
        } else {
          this.m_hashTable[hash] = this.m_pairs[index].next;
        }
        const pair = this.m_pairs[index];
        const userData = pair.userData;
        pair.next = this.m_freePair;
        pair.proxyId1 = b2Pair.b2_nullProxy;
        pair.proxyId2 = b2Pair.b2_nullProxy;
        pair.userData = null;
        pair.status = 0;
        this.m_freePair = index;
        this.m_pairCount -= 1;
        return userData;
      }
      prevIndex = index;
      index = this.m_pairs[index].next;
    }
    return null;
  }

  AddBufferedPair(id1, id2) {
    const pair = this._addPair(id1, id2);
    if (!pair.IsBuffered()) {
      pair.SetBuffered();
      const buf = this.m_pairBuffer[this.m_pairBufferCount];
      buf.proxyId1 = pair.proxyId1;
      buf.proxyId2 = pair.proxyId2;
      this.m_pairBufferCount += 1;
    }
    pair.ClearRemoved();
  }

  RemoveBufferedPair(id1, id2) {
    const pair = this._find(id1, id2);
    if (pair === null) { return; }
    if (!pair.IsBuffered()) {
      pair.SetBuffered();
      const buf = this.m_pairBuffer[this.m_pairBufferCount];
      buf.proxyId1 = pair.proxyId1;
      buf.proxyId2 = pair.proxyId2;
      this.m_pairBufferCount += 1;
    }
    pair.SetRemoved();
  }

  Commit() {
    const pool = this.m_broadPhase.m_proxyPool;
    let removeCount = 0;

    // first pass: fire callbacks for new/removed pairs
    for (let i = 0; i < this.m_pairBufferCount; i++) {
      const buf = this.m_pairBuffer[i];
      const pair = this._find(buf.proxyId1, buf.proxyId2);
      pair.ClearBuffered();
      const proxy1 = pool[pair.proxyId1];
      const proxy2 = pool[pair.proxyId2];
      if (pair.IsRemoved()) {
        if (pair.IsFinal()) {
          this.m_callback.PairRemoved(proxy1.userData, proxy2.userData, pair.userData);
        }
        // stage for removal — reuse pairBuffer
        this.m_pairBuffer[removeCount].proxyId1 = pair.proxyId1;
        this.m_pairBuffer[removeCount].proxyId2 = pair.proxyId2;
        removeCount += 1;
      } else if (!pair.IsFinal()) {
        pair.userData = this.m_callback.PairAdded(proxy1.userData, proxy2.userData);
        pair.SetFinal();
      }
    }

    // second pass: actually remove staged pairs
    for (let i = 0; i < removeCount; i++) {
      const buf = this.m_pairBuffer[i];
      this._removePair(buf.proxyId1, buf.proxyId2);
    }

    this.m_pairBufferCount = 0;
  }
}

// Full AS3 b2BroadPhase — sorted-bounds sweep-and-prune
class b2BroadPhase {
  static s_validate = false;
  static b2_invalid = 0xffff;  // USHRT_MAX used as sentinel
  static b2_nullEdge = 0xffff;

  constructor(worldAABB, pairCallback) {
    this.m_pairManager = new b2PairManager();
    this.m_worldAABB = worldAABB;
    this.m_proxyCount = 0;
    this.m_timeStamp = 1;
    this.m_queryResultCount = 0;
    this.m_queryResults = new Int32Array(512);

    // Two axis bound arrays, each of length 2*b2_maxProxies entries
    const maxProxies = 512;
    this.m_bounds = [
      new Array(2 * maxProxies),
      new Array(2 * maxProxies),
    ];
    for (let axis = 0; axis < 2; axis++) {
      for (let i = 0; i < 2 * maxProxies; i++) {
        this.m_bounds[axis][i] = new b2Bound();
      }
    }

    // Proxy pool
    this.m_proxyPool = new Array(maxProxies);
    for (let i = 0; i < maxProxies - 1; i++) {
      const p = new b2Proxy();
      p.SetNext(i + 1);
      p.timeStamp = 0;
      p.overlapCount = b2BroadPhase.b2_invalid;
      this.m_proxyPool[i] = p;
    }
    const last = new b2Proxy();
    last.SetNext(b2Pair.b2_nullProxy);
    last.timeStamp = 0;
    last.overlapCount = b2BroadPhase.b2_invalid;
    this.m_proxyPool[maxProxies - 1] = last;
    this.m_freeProxy = 0;

    // Quantization factor
    const w = worldAABB.upperBound.x - worldAABB.lowerBound.x;
    const h = worldAABB.upperBound.y - worldAABB.lowerBound.y;
    this.m_quantizationFactor = { x: 65535 / w, y: 65535 / h };

    this.m_pairManager.Initialize(this, pairCallback);
  }

  // Convert world AABB to quantized integer bounds
  _computeBounds(lv, uv, aabb) {
    let lx = aabb.lowerBound.x;
    let ly = aabb.lowerBound.y;
    lx = Math.min(lx, this.m_worldAABB.upperBound.x);
    ly = Math.min(ly, this.m_worldAABB.upperBound.y);
    lx = Math.max(lx, this.m_worldAABB.lowerBound.x);
    ly = Math.max(ly, this.m_worldAABB.lowerBound.y);

    let ux = aabb.upperBound.x;
    let uy = aabb.upperBound.y;
    ux = Math.min(ux, this.m_worldAABB.upperBound.x);
    uy = Math.min(uy, this.m_worldAABB.upperBound.y);
    ux = Math.max(ux, this.m_worldAABB.lowerBound.x);
    uy = Math.max(uy, this.m_worldAABB.lowerBound.y);

    // Lower: even (bit0=0), Upper: odd (bit0=1)
    lv[0] = (((this.m_quantizationFactor.x * (lx - this.m_worldAABB.lowerBound.x)) >>> 0) & 0xFFFE);
    uv[0] = (((this.m_quantizationFactor.x * (ux - this.m_worldAABB.lowerBound.x)) >>> 0) & 0xFFFF) | 1;
    lv[1] = (((this.m_quantizationFactor.y * (ly - this.m_worldAABB.lowerBound.y)) >>> 0) & 0xFFFE);
    uv[1] = (((this.m_quantizationFactor.y * (uy - this.m_worldAABB.lowerBound.y)) >>> 0) & 0xFFFF) | 1;
  }

  static BinarySearch(bounds, count, value) {
    let lo = 0;
    let hi = count - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (bounds[mid].value > value) {
        hi = mid - 1;
      } else if (bounds[mid].value >= value) {
        return mid;
      } else {
        lo = mid + 1;
      }
    }
    return lo;
  }

  _query(lowerOut, upperOut, lv, uv, bounds, count, axis) {
    const lo = b2BroadPhase.BinarySearch(bounds, count, lv);
    const hi = b2BroadPhase.BinarySearch(bounds, count, uv);

    // Sweep forward from lo to hi — overlapping lower bounds
    for (let i = lo; i < hi; i++) {
      if (bounds[i].IsLower()) {
        this._incrementOverlapCount(bounds[i].proxyId);
      }
    }

    // Walk stabbers left of lo
    if (lo > 0) {
      let idx = lo - 1;
      let stab = bounds[idx].stabbingCount;
      while (stab > 0) {
        const b = bounds[idx];
        if (b.IsLower()) {
          const proxy = this.m_proxyPool[b.proxyId];
          if (lo <= proxy.upperBounds[axis]) {
            this._incrementOverlapCount(b.proxyId);
            stab -= 1;
          }
        }
        idx -= 1;
        if (idx < 0) break;
      }
    }

    lowerOut[0] = lo;
    upperOut[0] = hi;
  }

  _incrementOverlapCount(proxyId) {
    const proxy = this.m_proxyPool[proxyId];
    if (proxy.timeStamp < this.m_timeStamp) {
      proxy.timeStamp = this.m_timeStamp;
      proxy.overlapCount = 1;
    } else {
      proxy.overlapCount = 2;
      this.m_queryResults[this.m_queryResultCount++] = proxyId;
    }
  }

  _incrementTimeStamp() {
    if (this.m_timeStamp === 65535) {
      for (let i = 0; i < this.m_proxyPool.length; i++) {
        this.m_proxyPool[i].timeStamp = 0;
      }
      this.m_timeStamp = 1;
    } else {
      this.m_timeStamp += 1;
    }
  }

  _testOverlap(boundValues, proxy) {
    for (let axis = 0; axis < 2; axis++) {
      const bounds = this.m_bounds[axis];
      if (boundValues.lowerValues[axis] > bounds[proxy.upperBounds[axis]].value) return false;
      if (boundValues.upperValues[axis] < bounds[proxy.lowerBounds[axis]].value) return false;
    }
    return true;
  }

  _testOverlapOld(bv1, proxy) {
    // convenience: accepts lowerValues/upperValues arrays
    for (let axis = 0; axis < 2; axis++) {
      const axBounds = this.m_bounds[axis];
      if (bv1.lowerValues[axis] > axBounds[proxy.upperBounds[axis]].value) return false;
      if (bv1.upperValues[axis] < axBounds[proxy.lowerBounds[axis]].value) return false;
    }
    return true;
  }

  InRange(aabb) {
    const lx = aabb.lowerBound.x - this.m_worldAABB.upperBound.x;
    const ly = aabb.lowerBound.y - this.m_worldAABB.upperBound.y;
    const ux = this.m_worldAABB.lowerBound.x - aabb.upperBound.x;
    const uy = this.m_worldAABB.lowerBound.y - aabb.upperBound.y;
    return Math.max(Math.max(lx, ux), Math.max(ly, uy)) < 0;
  }

  CreateProxy(aabb, userData) {
    const proxyId = this.m_freeProxy;
    const proxy = this.m_proxyPool[proxyId];
    this.m_freeProxy = proxy.GetNext();
    proxy.overlapCount = 0;
    proxy.userData = userData;

    const edgeCount = 2 * this.m_proxyCount;
    const lv = [0, 0];
    const uv = [0, 0];
    this._computeBounds(lv, uv, aabb);

    for (let axis = 0; axis < 2; axis++) {
      const bounds = this.m_bounds[axis];
      const loOut = [0];
      const hiOut = [0];
      this._query(loOut, hiOut, lv[axis], uv[axis], bounds, edgeCount, axis);
      let lo = loOut[0];
      let hi = hiOut[0];

      // Shift [hi, edgeCount) right by 2 to make room for new lower+upper
      const tailCount = edgeCount - hi;
      const tail = new Array(tailCount);
      for (let i = 0; i < tailCount; i++) {
        const src = bounds[hi + i];
        tail[i] = { value: src.value, proxyId: src.proxyId, stabbingCount: src.stabbingCount };
      }
      for (let i = 0; i < tailCount; i++) {
        const dst = bounds[hi + 2 + i];
        dst.value = tail[i].value;
        dst.proxyId = tail[i].proxyId;
        dst.stabbingCount = tail[i].stabbingCount;
      }

      // Shift [lo, hi) right by 1 to make room for new lower
      const midCount = hi - lo;
      const mid = new Array(midCount);
      for (let i = 0; i < midCount; i++) {
        const src = bounds[lo + i];
        mid[i] = { value: src.value, proxyId: src.proxyId, stabbingCount: src.stabbingCount };
      }
      for (let i = 0; i < midCount; i++) {
        const dst = bounds[lo + 1 + i];
        dst.value = mid[i].value;
        dst.proxyId = mid[i].proxyId;
        dst.stabbingCount = mid[i].stabbingCount;
      }

      // Insert lower bound at lo, upper bound at hi+1
      hi += 1;
      const lowerBound = bounds[lo];
      const upperBound = bounds[hi];
      lowerBound.value = lv[axis];
      lowerBound.proxyId = proxyId;
      upperBound.value = uv[axis];
      upperBound.proxyId = proxyId;

      // stabbingCount for lower = count of intervals stabbing position lo
      lowerBound.stabbingCount = lo === 0 ? 0 : bounds[lo - 1].stabbingCount;
      upperBound.stabbingCount = bounds[hi - 1].stabbingCount;

      // Increment stabbing counts between lo and hi
      for (let i = lo; i < hi; i++) {
        bounds[i].stabbingCount += 1;
      }

      // Update proxy indices for all affected entries
      for (let i = lo; i < edgeCount + 2; i++) {
        const b = bounds[i];
        const p = this.m_proxyPool[b.proxyId];
        if (b.IsLower()) {
          p.lowerBounds[axis] = i;
        } else {
          p.upperBounds[axis] = i;
        }
      }

      proxy.lowerBounds[axis] = lo;
      proxy.upperBounds[axis] = hi;
    }

    this.m_proxyCount += 1;

    // Pair the new proxy with all overlapping proxies found during query
    for (let i = 0; i < this.m_queryResultCount; i++) {
      this.m_pairManager.AddBufferedPair(proxyId, this.m_queryResults[i]);
    }
    this.m_pairManager.Commit();
    this.m_queryResultCount = 0;
    this._incrementTimeStamp();

    return proxy;  // return proxy object (userData held inside)
  }

  DestroyProxy(proxyOrId) {
    // Accept either the proxy object or a numeric id
    let proxyId;
    if (typeof proxyOrId === "number") {
      proxyId = proxyOrId;
    } else {
      // Find by identity in pool
      proxyId = this.m_proxyPool.indexOf(proxyOrId);
      if (proxyId === -1) return;
    }

    const proxy = this.m_proxyPool[proxyId];
    const edgeCount = 2 * this.m_proxyCount;

    for (let axis = 0; axis < 2; axis++) {
      const bounds = this.m_bounds[axis];
      const lo = proxy.lowerBounds[axis];
      const hi = proxy.upperBounds[axis];
      const lv = bounds[lo].value;
      const uv = bounds[hi].value;

      // Remove upper bound: shift [lo+1, hi) left by 1
      const midCount = hi - lo - 1;
      const mid = new Array(midCount);
      for (let i = 0; i < midCount; i++) {
        const src = bounds[lo + 1 + i];
        mid[i] = { value: src.value, proxyId: src.proxyId, stabbingCount: src.stabbingCount };
      }
      for (let i = 0; i < midCount; i++) {
        const dst = bounds[lo + i];
        dst.value = mid[i].value;
        dst.proxyId = mid[i].proxyId;
        dst.stabbingCount = mid[i].stabbingCount;
      }

      // Remove lower bound: shift [hi+1, edgeCount) left by 1
      const tailCount = edgeCount - hi - 1;
      const tail = new Array(tailCount);
      for (let i = 0; i < tailCount; i++) {
        const src = bounds[hi + 1 + i];
        tail[i] = { value: src.value, proxyId: src.proxyId, stabbingCount: src.stabbingCount };
      }
      for (let i = 0; i < tailCount; i++) {
        const dst = bounds[hi - 1 + i];
        dst.value = tail[i].value;
        dst.proxyId = tail[i].proxyId;
        dst.stabbingCount = tail[i].stabbingCount;
      }

      // Update proxy lower/upper indices
      for (let i = lo; i < edgeCount - 2; i++) {
        const b = bounds[i];
        const p = this.m_proxyPool[b.proxyId];
        if (b.IsLower()) {
          p.lowerBounds[axis] = i;
        } else {
          p.upperBounds[axis] = i;
        }
      }

      // Decrement stabbing counts between lo and hi-1
      for (let i = lo; i < hi - 1; i++) {
        bounds[i].stabbingCount -= 1;
      }

      // Query the removed interval for pairs to remove
      const loOut = [0];
      const hiOut = [0];
      this._query(loOut, hiOut, lv, uv, bounds, edgeCount - 2, axis);
    }

    // Buffer pair removals
    for (let i = 0; i < this.m_queryResultCount; i++) {
      this.m_pairManager.RemoveBufferedPair(proxyId, this.m_queryResults[i]);
    }
    this.m_pairManager.Commit();
    this.m_queryResultCount = 0;
    this._incrementTimeStamp();

    // Return proxy to free list
    proxy.userData = null;
    proxy.overlapCount = b2BroadPhase.b2_invalid;
    proxy.lowerBounds[0] = b2BroadPhase.b2_invalid;
    proxy.lowerBounds[1] = b2BroadPhase.b2_invalid;
    proxy.upperBounds[0] = b2BroadPhase.b2_invalid;
    proxy.upperBounds[1] = b2BroadPhase.b2_invalid;
    proxy.SetNext(this.m_freeProxy);
    this.m_freeProxy = proxyId;
    this.m_proxyCount -= 1;
  }

  MoveProxy(proxyOrId, aabb) {
    let proxyId;
    if (typeof proxyOrId === "number") {
      proxyId = proxyOrId;
    } else {
      proxyId = this.m_proxyPool.indexOf(proxyOrId);
      if (proxyId === -1) return;
    }

    if (!aabb.IsValid?.()) {
      return;
    }

    const proxy = this.m_proxyPool[proxyId];
    const edgeCount = 2 * this.m_proxyCount;

    // New quantized bounds
    const newBV = new b2BoundValues();
    this._computeBounds(newBV.lowerValues, newBV.upperValues, aabb);

    // Old quantized bounds (read from current bound arrays)
    const oldBV = new b2BoundValues();
    for (let axis = 0; axis < 2; axis++) {
      oldBV.lowerValues[axis] = this.m_bounds[axis][proxy.lowerBounds[axis]].value;
      oldBV.upperValues[axis] = this.m_bounds[axis][proxy.upperBounds[axis]].value;
    }

    for (let axis = 0; axis < 2; axis++) {
      const axBounds = this.m_bounds[axis];
      let lo = proxy.lowerBounds[axis];
      let hi = proxy.upperBounds[axis];
      const newLo = newBV.lowerValues[axis];
      const newHi = newBV.upperValues[axis];
      const deltaLo = newLo - axBounds[lo].value;
      const deltaHi = newHi - axBounds[hi].value;
      axBounds[lo].value = newLo;
      axBounds[hi].value = newHi;

      // Move lower bound left (decrease)
      if (deltaLo < 0) {
        let idx = lo;
        while (idx > 0 && newLo < axBounds[idx - 1].value) {
          const cur = axBounds[idx];
          const prev = axBounds[idx - 1];
          const prevProxyId = prev.proxyId;
          const prevProxy = this.m_proxyPool[prevProxyId];
          prev.stabbingCount += 1;
          if (prev.IsUpper()) {
            if (this._testOverlap(newBV, prevProxy)) {
              this.m_pairManager.AddBufferedPair(proxyId, prevProxyId);
            }
            prevProxy.upperBounds[axis] += 1;
            cur.stabbingCount += 1;
          } else {
            prevProxy.lowerBounds[axis] += 1;
            cur.stabbingCount -= 1;
          }
          proxy.lowerBounds[axis] -= 1;
          cur.Swap(prev);
          idx -= 1;
        }
      }

      // Move upper bound right (increase)
      if (deltaHi > 0) {
        let idx = hi;
        while (idx < edgeCount - 1 && axBounds[idx + 1].value <= newHi) {
          const cur = axBounds[idx];
          const next = axBounds[idx + 1];
          const nextProxyId = next.proxyId;
          const nextProxy = this.m_proxyPool[nextProxyId];
          next.stabbingCount += 1;
          if (next.IsLower()) {
            if (this._testOverlap(newBV, nextProxy)) {
              this.m_pairManager.AddBufferedPair(proxyId, nextProxyId);
            }
            nextProxy.lowerBounds[axis] -= 1;
            cur.stabbingCount += 1;
          } else {
            nextProxy.upperBounds[axis] -= 1;
            cur.stabbingCount -= 1;
          }
          proxy.upperBounds[axis] += 1;
          cur.Swap(next);
          idx += 1;
        }
      }

      // Move lower bound right (increase)
      if (deltaLo > 0) {
        let idx = lo;
        while (idx < edgeCount - 1 && axBounds[idx + 1].value <= newLo) {
          const cur = axBounds[idx];
          const next = axBounds[idx + 1];
          const nextProxyId = next.proxyId;
          const nextProxy = this.m_proxyPool[nextProxyId];
          next.stabbingCount -= 1;
          if (next.IsUpper()) {
            if (this._testOverlap(oldBV, nextProxy)) {
              this.m_pairManager.RemoveBufferedPair(proxyId, nextProxyId);
            }
            nextProxy.upperBounds[axis] -= 1;
            cur.stabbingCount -= 1;
          } else {
            nextProxy.lowerBounds[axis] -= 1;
            cur.stabbingCount += 1;
          }
          proxy.lowerBounds[axis] += 1;
          cur.Swap(next);
          idx += 1;
        }
      }

      // Move upper bound left (decrease)
      if (deltaHi < 0) {
        let idx = hi;
        while (idx > 0 && newHi < axBounds[idx - 1].value) {
          const cur = axBounds[idx];
          const prev = axBounds[idx - 1];
          const prevProxyId = prev.proxyId;
          const prevProxy = this.m_proxyPool[prevProxyId];
          prev.stabbingCount -= 1;
          if (prev.IsLower()) {
            if (this._testOverlap(oldBV, prevProxy)) {
              this.m_pairManager.RemoveBufferedPair(proxyId, prevProxyId);
            }
            prevProxy.lowerBounds[axis] += 1;
            cur.stabbingCount += 1;
          } else {
            prevProxy.upperBounds[axis] += 1;
            cur.stabbingCount -= 1;
          }
          proxy.upperBounds[axis] -= 1;
          cur.Swap(prev);
          idx -= 1;
        }
      }
    }
  }

  Commit() {
    this.m_pairManager.Commit();
  }

  QueryAABB(aabb, out = [], maxCount = 512) {
    const lv = [0, 0];
    const uv = [0, 0];
    this._computeBounds(lv, uv, aabb);
    let count = 0;
    for (let proxyId = 0; proxyId < this.m_proxyPool.length && count < maxCount; proxyId += 1) {
      const proxy = this.m_proxyPool[proxyId];
      if (!proxy?.IsValid?.() || proxy.userData == null) {
        continue;
      }
      const lowerX = this.m_bounds[0][proxy.lowerBounds[0]].value;
      const upperX = this.m_bounds[0][proxy.upperBounds[0]].value;
      if (lv[0] > upperX || uv[0] < lowerX) {
        continue;
      }
      const lowerY = this.m_bounds[1][proxy.lowerBounds[1]].value;
      const upperY = this.m_bounds[1][proxy.upperBounds[1]].value;
      if (lv[1] > upperY || uv[1] < lowerY) {
        continue;
      }
      out[count++] = proxy.userData;
    }
    this.m_queryResultCount = 0;
    this._incrementTimeStamp();
    return count;
  }

  // No UpdatePairs — contacts are managed via PairCallback (PairAdded/PairRemoved)
}

export class b2FixtureDef {
  constructor() {
    this.shape = null;
    this.userData = null;
    this.friction = 0.2;
    this.restitution = 0;
    this.density = 0;
    this.isSensor = false;
    this.filter = new b2FilterData();
  }
}

export class b2Fixture {
  constructor(body, def) {
    this.m_body = body;
    this.m_shape = def.shape;
    this.m_shape.m_body = body;
    this.m_userData = def.userData ?? this.m_shape.m_userData ?? null;
    this.m_friction = def.friction;
    this.m_restitution = def.restitution;
    this.m_density = def.density;
    this.m_isSensor = Boolean(def.isSensor);
    this.m_filter = def.filter.Copy();
    this.m_next = null;
    this.m_proxy = null;
    this.m_shape.m_density = this.m_density;
    this.m_shape.m_friction = this.m_friction;
    this.m_shape.m_restitution = this.m_restitution;
    this.m_shape.m_isSensor = this.m_isSensor;
    this.m_shape.m_filter = this.m_filter.Copy();
  }

  CreateProxy(broadPhase, xf) {
    const aabb = new b2AABB();
    this.m_shape.ComputeAABB(aabb, xf);
    if (broadPhase.InRange(aabb)) {
      this.m_proxy = broadPhase.CreateProxy(aabb, this);
      this.m_shape.m_proxyId = this.m_proxy;
    }
  }

  DestroyProxy(broadPhase) {
    if (this.m_proxy) {
      broadPhase.DestroyProxy(this.m_proxy);
      this.m_proxy = null;
      this.m_shape.m_proxyId = null;
    }
  }

  Synchronize(broadPhase, xf1, xf2) {
    const aabb = new b2AABB();
    this.m_shape.ComputeSweptAABB(aabb, xf1, xf2);
    if (!broadPhase.InRange(aabb)) {
      return false;
    }
    if (!this.m_proxy) {
      this.m_proxy = broadPhase.CreateProxy(aabb, this);
    } else {
      broadPhase.MoveProxy(this.m_proxy, aabb);
    }
    this.m_shape.m_proxyId = this.m_proxy;
    return true;
  }

  RefilterProxy(broadPhase, xf) {
    this.DestroyProxy(broadPhase);
    this.CreateProxy(broadPhase, xf);
  }

  GetBody() {
    return this.m_body;
  }

  GetShape() {
    return this.m_shape;
  }

  GetType() {
    return this.m_shape.GetType();
  }

  IsSensor() {
    return this.m_isSensor;
  }

  GetFilterData() {
    return this.m_filter.Copy();
  }

  SetFilterData(filter) {
    this.m_filter = filter.Copy();
    this.m_shape.m_filter = this.m_filter.Copy();
    let edge = this.m_body.GetContactList();
    while (edge) {
      const contact = edge.contact;
      if (contact.GetFixtureA() === this || contact.GetFixtureB() === this) {
        contact.FlagForFiltering();
      }
      edge = edge.next;
    }
  }

  GetNext() {
    return this.m_next;
  }

  GetUserData() {
    return this.m_userData;
  }

  SetUserData(userData) {
    this.m_userData = userData;
  }
}

export class b2Body {
  static e_awakeFlag = 0;
  static e_frozenFlag = 2;
  static e_islandFlag = 4;
  static e_sleepFlag = 8;
  static e_allowSleepFlag = 16;
  static e_bulletFlag = 32;
  static e_fixedRotationFlag = 64;
  static e_staticType = 1;
  static e_dynamicType = 2;
  static e_maxTypes = 3;
  static b2_staticBody = 0;
  static b2_kinematicBody = 1;
  static b2_dynamicBody = 2;
  static s_massData = new b2MassData();
  static s_xf1 = new b2XForm();

  constructor(def, world) {
    this.m_flags = 0;
    if (def.isBullet) {
      this.m_flags |= b2Body.e_bulletFlag;
    }
    if (def.fixedRotation) {
      this.m_flags |= b2Body.e_fixedRotationFlag;
    }
    if (def.allowSleep) {
      this.m_flags |= b2Body.e_allowSleepFlag;
    }
    if (def.isSleeping || def.awake === false) {
      this.m_flags |= b2Body.e_sleepFlag;
    }
    this.m_world = world;
    this.m_xf = new b2XForm();
    this.m_xf.position.SetV(def.position);
    this.m_xf.R.Set(def.angle);
    this.m_sweep = new b2Sweep();
    this.m_sweep.localCenter.SetV(def.massData.center);
    this.m_sweep.t0 = 1;
    this.m_sweep.a0 = def.angle;
    this.m_sweep.a = def.angle;
    const rotation = this.m_xf.R;
    const center = this.m_sweep.localCenter;
    this.m_sweep.c.x = rotation.col1.x * center.x + rotation.col2.x * center.y + this.m_xf.position.x;
    this.m_sweep.c.y = rotation.col1.y * center.x + rotation.col2.y * center.y + this.m_xf.position.y;
    this.m_sweep.c0.SetV(this.m_sweep.c);
    this.m_jointList = null;
    this.m_contactList = null;
    this.m_prev = null;
    this.m_next = null;
    this.m_linearDamping = b2Scalar(def.linearDamping);
    this.m_angularDamping = b2Scalar(def.angularDamping);
    this.m_force = new b2Vec2();
    this.m_force.SetZero();
    this.m_torque = 0;
    this.m_linearVelocity = new b2Vec2();
    this.m_linearVelocity.SetZero();
    this.m_angularVelocity = 0;
    this.m_sleepTime = 0;
    this.m_invMass = 0;
    this.m_I = 0;
    this.m_invI = 0;
    this.m_mass = b2Scalar(def.massData.mass);
    if (this.m_mass > 0) {
      this.m_invMass = b2Scalar(1 / this.m_mass);
    }
    if ((this.m_flags & b2Body.e_fixedRotationFlag) === 0) {
      this.m_I = b2Scalar(def.massData.I);
    }
    if (this.m_I > 0) {
      this.m_invI = b2Scalar(1 / this.m_I);
    }
    this.m_type = (this.m_invMass === 0 && this.m_invI === 0) ? b2Body.e_staticType : b2Body.e_dynamicType;
    if (def.type === b2Body.b2_staticBody) {
      this.m_type = b2Body.e_staticType;
      this.m_mass = 0;
      this.m_invMass = 0;
      this.m_I = 0;
      this.m_invI = 0;
    }
    this.m_userData = def.userData;
    this.m_fixtureList = null;
    this.m_shapeList = null;
    this.m_fixtureCount = 0;
    this.m_shapeCount = 0;
  }

  IsStatic() {
    return this.m_type === b2Body.e_staticType;
  }

  IsDynamic() {
    return this.m_type === b2Body.e_dynamicType;
  }

  IsSleeping() {
    return (this.m_flags & b2Body.e_sleepFlag) === b2Body.e_sleepFlag;
  }

  IsAwake() {
    return !this.IsSleeping();
  }

  SetAwake(flag) {
    if (flag) {
      this.WakeUp();
    } else {
      this.PutToSleep();
    }
  }

  WakeUp() {
    this.m_flags &= ~b2Body.e_sleepFlag;
    this.m_sleepTime = 0;
  }

  PutToSleep() {
    this.m_flags |= b2Body.e_sleepFlag;
    this.m_sleepTime = 0;
    this.m_linearVelocity.SetZero();
    this.m_angularVelocity = 0;
    this.m_force.SetZero();
    this.m_torque = 0;
  }

  GetType() {
    return this.IsStatic() ? b2Body.b2_staticBody : b2Body.b2_dynamicBody;
  }

  GetWorld() {
    return this.m_world;
  }

  GetTransform() {
    return this.m_xf;
  }

  GetXForm() {
    return this.m_xf;
  }

  GetPosition() {
    return this.m_xf.position;
  }

  GetAngle() {
    return this.m_sweep.a;
  }

  GetWorldCenter() {
    return this.m_sweep.c;
  }

  GetLocalCenter() {
    return this.m_sweep.localCenter;
  }

  GetLinearVelocity() {
    return this.m_linearVelocity;
  }

  GetAngularVelocity() {
    return this.m_angularVelocity;
  }

  SetLinearVelocity(value) {
    this.m_linearVelocity.SetV(value);
  }

  SetAngularVelocity(value) {
    this.m_angularVelocity = b2Scalar(value);
  }

  SetLinearDamping(value) {
    this.m_linearDamping = b2Scalar(value);
  }

  SetAngularDamping(value) {
    this.m_angularDamping = b2Scalar(value);
  }

  GetUserData() {
    return this.m_userData;
  }

  SetUserData(userData) {
    this.m_userData = userData;
  }

  IsBullet() {
    return (this.m_flags & b2Body.e_bulletFlag) === b2Body.e_bulletFlag;
  }

  SetBullet(flag) {
    if (flag) {
      this.m_flags |= b2Body.e_bulletFlag;
    } else {
      this.m_flags &= ~b2Body.e_bulletFlag;
    }
  }

  GetMass() {
    return this.m_mass;
  }

  GetInertia() {
    return this.m_I;
  }

  GetFixtureList() {
    return this.m_fixtureList;
  }

  GetShapeList() {
    return this.m_shapeList;
  }

  GetJointList() {
    return this.m_jointList;
  }

  GetContactList() {
    return this.m_contactList;
  }

  GetNext() {
    return this.m_next;
  }

  AllowSleeping(flag) {
    if (flag) {
      this.m_flags |= b2Body.e_allowSleepFlag;
    } else {
      this.m_flags &= ~b2Body.e_allowSleepFlag;
      this.WakeUp();
    }
  }

  ApplyForce(force, point) {
    if (this.IsSleeping()) {
      this.WakeUp();
    }
    this.m_force.x = b2Scalar(this.m_force.x + force.x);
    this.m_force.y = b2Scalar(this.m_force.y + force.y);
    this.m_torque = b2Scalar(this.m_torque + ((point.x - this.m_sweep.c.x) * force.y - (point.y - this.m_sweep.c.y) * force.x));
  }

  ApplyTorque(torque) {
    if (this.IsSleeping()) {
      this.WakeUp();
    }
    this.m_torque = b2Scalar(this.m_torque + torque);
  }

  ApplyImpulse(impulse, point) {
    if (this.IsSleeping()) {
      this.WakeUp();
    }
    this.m_linearVelocity.x = b2Scalar(this.m_linearVelocity.x + this.m_invMass * impulse.x);
    this.m_linearVelocity.y = b2Scalar(this.m_linearVelocity.y + this.m_invMass * impulse.y);
    this.m_angularVelocity = b2Scalar(this.m_angularVelocity + this.m_invI * ((point.x - this.m_sweep.c.x) * impulse.y - (point.y - this.m_sweep.c.y) * impulse.x));
  }

  GetLocalPoint(point) {
    return b2Math.b2MulXT(this.m_xf, point);
  }

  GetWorldPoint(point) {
    return b2Math.b2MulX(this.m_xf, point);
  }

  GetLocalVector(vector) {
    return b2Math.b2MulTMV(this.m_xf.R, vector);
  }

  GetWorldVector(vector) {
    return b2Math.b2MulMV(this.m_xf.R, vector);
  }

  GetLinearVelocityFromWorldPoint(point) {
    return new b2Vec2(
      this.m_linearVelocity.x - this.m_angularVelocity * (point.y - this.m_sweep.c.y),
      this.m_linearVelocity.y + this.m_angularVelocity * (point.x - this.m_sweep.c.x),
    );
  }

  GetLinearVelocityFromLocalPoint(point) {
    const worldPoint = this.GetWorldPoint(point);
    return new b2Vec2(
      this.m_linearVelocity.x - this.m_angularVelocity * (worldPoint.y - this.m_sweep.c.y),
      this.m_linearVelocity.y + this.m_angularVelocity * (worldPoint.x - this.m_sweep.c.x),
    );
  }

  SynchronizeTransform() {
    this.m_xf.R.Set(this.m_sweep.a);
    const rotation = this.m_xf.R;
    const center = this.m_sweep.localCenter;
    this.m_xf.position.x = b2Scalar(this.m_sweep.c.x - (rotation.col1.x * center.x + rotation.col2.x * center.y));
    this.m_xf.position.y = b2Scalar(this.m_sweep.c.y - (rotation.col1.y * center.x + rotation.col2.y * center.y));
  }

  SynchronizeShapes() {
    const xf1 = b2Body.s_xf1;
    xf1.R.Set(this.m_sweep.a0);
    const rotation = xf1.R;
    const center = this.m_sweep.localCenter;
    xf1.position.x = b2Scalar(this.m_sweep.c0.x - (rotation.col1.x * center.x + rotation.col2.x * center.y));
    xf1.position.y = b2Scalar(this.m_sweep.c0.y - (rotation.col1.y * center.x + rotation.col2.y * center.y));
    let fixture = this.m_fixtureList;
    while (fixture) {
      const okay = fixture.Synchronize(this.m_world.m_broadPhase, xf1, this.m_xf);
      if (!okay) {
        this.m_flags |= b2Body.e_frozenFlag;
        this.m_linearVelocity.SetZero();
        this.m_angularVelocity = 0;
        let destroyFixture = this.m_fixtureList;
        while (destroyFixture) {
          destroyFixture.DestroyProxy(this.m_world.m_broadPhase);
          destroyFixture = destroyFixture.m_next;
        }
        return false;
      }
      fixture = fixture.m_next;
    }
    return true;
  }

  Advance(t) {
    this.m_sweep.Advance(t);
    this.m_sweep.c.SetV(this.m_sweep.c0);
    this.m_sweep.a = this.m_sweep.a0;
    this.SynchronizeTransform();
  }

  SetMass(massData) {
    if (this.m_world.m_lock === true) {
      return;
    }
    const previousType = this.m_type;
    this.m_invMass = 0;
    this.m_I = 0;
    this.m_invI = 0;
    this.m_mass = b2Scalar(massData.mass);
    if (this.m_mass > 0) {
      this.m_invMass = b2Scalar(1 / this.m_mass);
    }
    if ((this.m_flags & b2Body.e_fixedRotationFlag) === 0) {
      this.m_I = b2Scalar(massData.I);
    }
    if (this.m_I > 0) {
      this.m_invI = b2Scalar(1 / this.m_I);
    }
    this.m_sweep.localCenter.SetV(massData.center);
    const rotation = this.m_xf.R;
    const center = this.m_sweep.localCenter;
    this.m_sweep.c.x = b2Scalar(rotation.col1.x * center.x + rotation.col2.x * center.y + this.m_xf.position.x);
    this.m_sweep.c.y = b2Scalar(rotation.col1.y * center.x + rotation.col2.y * center.y + this.m_xf.position.y);
    this.m_sweep.c0.SetV(this.m_sweep.c);
    let fixture = this.m_fixtureList;
    while (fixture) {
      fixture.m_shape.UpdateSweepRadius(this.m_sweep.localCenter);
      fixture = fixture.m_next;
    }
    this.m_type = (this.m_invMass === 0 && this.m_invI === 0) ? b2Body.e_staticType : b2Body.e_dynamicType;
    if (previousType !== this.m_type) {
      fixture = this.m_fixtureList;
      while (fixture) {
        fixture.RefilterProxy(this.m_world.m_broadPhase, this.m_xf);
        fixture = fixture.m_next;
      }
    }
  }

  ResetMassData() {
    this.SetMassFromShapes();
  }

  SetMassFromShapes() {
    if (this.m_world.m_lock === true) {
      return;
    }
    const previousType = this.m_type;
    this.m_mass = 0;
    this.m_invMass = 0;
    this.m_I = 0;
    this.m_invI = 0;
    let centerX = 0;
    let centerY = 0;
    const massData = b2Body.s_massData;
    let fixture = this.m_fixtureList;
    while (fixture) {
      fixture.m_shape.ComputeMass(massData);
      this.m_mass = b2Scalar(this.m_mass + massData.mass);
      centerX = b2Scalar(centerX + massData.mass * massData.center.x);
      centerY = b2Scalar(centerY + massData.mass * massData.center.y);
      this.m_I = b2Scalar(this.m_I + massData.I);
      fixture = fixture.m_next;
    }
    if (this.m_mass > 0) {
      this.m_invMass = b2Scalar(1 / this.m_mass);
      centerX = b2Scalar(centerX * this.m_invMass);
      centerY = b2Scalar(centerY * this.m_invMass);
    }
    if (this.m_I > 0 && (this.m_flags & b2Body.e_fixedRotationFlag) === 0) {
      this.m_I = b2Scalar(this.m_I - this.m_mass * (centerX * centerX + centerY * centerY));
      this.m_invI = b2Scalar(1 / this.m_I);
    } else {
      this.m_I = 0;
      this.m_invI = 0;
    }
    this.m_sweep.localCenter.Set(centerX, centerY);
    const rotation = this.m_xf.R;
    const center = this.m_sweep.localCenter;
    this.m_sweep.c.x = b2Scalar(rotation.col1.x * center.x + rotation.col2.x * center.y + this.m_xf.position.x);
    this.m_sweep.c.y = b2Scalar(rotation.col1.y * center.x + rotation.col2.y * center.y + this.m_xf.position.y);
    this.m_sweep.c0.SetV(this.m_sweep.c);
    fixture = this.m_fixtureList;
    while (fixture) {
      fixture.m_shape.UpdateSweepRadius(this.m_sweep.localCenter);
      fixture = fixture.m_next;
    }
    this.m_type = (this.m_invMass === 0 && this.m_invI === 0) ? b2Body.e_staticType : b2Body.e_dynamicType;
    if (previousType !== this.m_type) {
      fixture = this.m_fixtureList;
      while (fixture) {
        fixture.RefilterProxy(this.m_world.m_broadPhase, this.m_xf);
        fixture = fixture.m_next;
      }
    }
  }

  ShouldCollide(other) {
    if (this.IsStatic() && other.IsStatic()) {
      return false;
    }
    if (this.IsConnected(other)) {
      return false;
    }
    return true;
  }

  IsConnected(other) {
    let edge = this.m_jointList;
    while (edge) {
      if (edge.other === other) {
        return edge.joint.m_collideConnected === false;
      }
      edge = edge.next;
    }
    return false;
  }

  CreateFixture(def) {
    if (this.m_world.m_lock === true) {
      return null;
    }
    const fixture = new b2Fixture(this, def);
    fixture.m_next = this.m_fixtureList;
    this.m_fixtureList = fixture;
    this.m_fixtureCount += 1;
    this.m_shapeCount = this.m_fixtureCount;
    this.RebuildShapeList();
    fixture.CreateProxy(this.m_world.m_broadPhase, this.m_xf);
    fixture.m_shape.UpdateSweepRadius(this.m_sweep.localCenter);
    if ((fixture.m_density ?? 0) > 0) {
      this.SetMassFromShapes();
    }
    return fixture;
  }

  DestroyFixture(fixture) {
    if (this.m_world.m_lock === true) {
      return;
    }
    fixture.DestroyProxy(this.m_world.m_broadPhase);
    let previous = null;
    let current = this.m_fixtureList;
    while (current) {
      if (current === fixture) {
        if (previous) {
          previous.m_next = current.m_next;
        } else {
          this.m_fixtureList = current.m_next;
        }
        break;
      }
      previous = current;
      current = current.m_next;
    }
    this.m_fixtureCount -= 1;
    this.m_shapeCount = this.m_fixtureCount;
    this.RebuildShapeList();
    this.SetMassFromShapes();
  }

  RebuildShapeList() {
    this.m_shapeList = null;
    let previousShape = null;
    let fixture = this.m_fixtureList;
    while (fixture) {
      fixture.m_shape.m_next = null;
      if (!this.m_shapeList) {
        this.m_shapeList = fixture.m_shape;
      } else {
        previousShape.m_next = fixture.m_shape;
      }
      previousShape = fixture.m_shape;
      fixture = fixture.m_next;
    }
  }
}

export class b2World {
  static m_continuousPhysics = true;
  static m_warmStarting = true;
  static m_positionCorrection = true;
  static s_debugStepCounter = 0;
  static s_debugStepObserver = null;
  static e_locked = 0x0002;
  static s_timestep2 = {
    dt: 0,
    inv_dt: 0,
    maxIterations: 0,
    velocityIterations: 0,
    positionIterations: 0,
    dtRatio: 0,
    positionCorrection: true,
    warmStarting: true,
  };

  constructor(worldAABBOrGravity, gravityOrDoSleep, maybeDoSleep) {
    this.m_destructionListener = null;
    this.m_boundaryListener = null;
    this.m_contactFilter = b2ContactFilter.b2_defaultFilter;
    this.m_contactListener = null;
    this.m_debugDraw = null;
    this.m_bodyList = null;
    this.m_contactList = null;
    this.m_jointList = null;
    this.m_bodyCount = 0;
    this.m_contactCount = 0;
    this.m_jointCount = 0;
    this.m_positionIterationCount = 0;
    let worldAABB;
    let gravity;
    let doSleep;
    if (worldAABBOrGravity?.lowerBound && gravityOrDoSleep?.x != null) {
      worldAABB = worldAABBOrGravity;
      gravity = gravityOrDoSleep;
      doSleep = maybeDoSleep;
    } else {
      worldAABB = createDefaultWorldAABB();
      gravity = worldAABBOrGravity;
      doSleep = gravityOrDoSleep;
    }
    this.m_allowSleep = doSleep;
    this.m_gravity = gravity;
    this.m_lock = false;
    this.m_flags = 0;
    this.m_inv_dt0 = 0;
    this.m_contactManager = new b2ContactManager();
    this.m_contactManager.m_world = this;
    this.m_worldAABB = worldAABB;
    // Wire the real AS3 BroadPhase; it drives contact creation via PairCallback.
    this.m_broadPhase = new b2BroadPhase(worldAABB, this.m_contactManager);
    this.m_contactManager.m_contactFilter = this.m_contactFilter;
    this.m_contactManager.m_contactListener = this.m_contactListener;
    this.m_groundBody = this.CreateBody(new b2BodyDef());
  }


  SetContactListener(listener) {
    this.m_contactListener = listener;
    this.m_contactManager.m_contactListener = listener;
  }

  SetContactFilter(filter) {
    this.m_contactFilter = filter;
    this.m_contactManager.m_contactFilter = filter;
  }

  SetGravity(gravity) {
    this.m_gravity = new b2Vec2(gravity.x, gravity.y);
  }

  GetGroundBody() {
    return this.m_groundBody;
  }

  GetBodyList() {
    return this.m_bodyList;
  }

  GetJointList() {
    return this.m_jointList;
  }

  GetBodyCount() {
    return this.m_bodyCount;
  }

  GetJointCount() {
    return this.m_jointCount;
  }

  GetContactCount() {
    return this.m_contactCount;
  }

  CreateBody(def) {
    if (this.m_lock === true) {
      return null;
    }
    const body = new b2Body(def, this);
    body.m_prev = null;
    body.m_next = this.m_bodyList;
    if (this.m_bodyList) {
      this.m_bodyList.m_prev = body;
    }
    this.m_bodyList = body;
    this.m_bodyCount += 1;
    return body;
  }

  DestroyBody(body) {
    if (this.m_lock === true || !body) {
      return;
    }
    let jointEdge = body.m_jointList;
    while (jointEdge) {
      const next = jointEdge.next;
      this.DestroyJoint(jointEdge.joint);
      jointEdge = next;
    }
    let fixture = body.m_fixtureList;
    while (fixture) {
      const next = fixture.m_next;
      fixture.DestroyProxy(this.m_broadPhase);
      fixture = next;
    }
    let contactEdge = body.m_contactList;
    while (contactEdge) {
      const next = contactEdge.next;
      this.m_contactManager.Destroy(contactEdge.contact);
      contactEdge = next;
    }
    if (body.m_prev) {
      body.m_prev.m_next = body.m_next;
    }
    if (body.m_next) {
      body.m_next.m_prev = body.m_prev;
    }
    if (body === this.m_bodyList) {
      this.m_bodyList = body.m_next;
    }
    this.m_bodyCount -= 1;
  }

  CreateJoint(def) {
    const joint = b2Joint.Create(def);
    if (!joint) {
      return null;
    }
    joint.m_prev = null;
    joint.m_next = this.m_jointList;
    if (this.m_jointList) {
      this.m_jointList.m_prev = joint;
    }
    this.m_jointList = joint;
    this.m_jointCount += 1;
    joint.m_nodeA.joint = joint;
    joint.m_nodeA.other = joint.m_bodyB;
    joint.m_nodeA.prev = null;
    joint.m_nodeA.next = joint.m_bodyA.m_jointList;
    if (joint.m_bodyA.m_jointList) {
      joint.m_bodyA.m_jointList.prev = joint.m_nodeA;
    }
    joint.m_bodyA.m_jointList = joint.m_nodeA;
    joint.m_nodeB.joint = joint;
    joint.m_nodeB.other = joint.m_bodyA;
    joint.m_nodeB.prev = null;
    joint.m_nodeB.next = joint.m_bodyB.m_jointList;
    if (joint.m_bodyB.m_jointList) {
      joint.m_bodyB.m_jointList.prev = joint.m_nodeB;
    }
    joint.m_bodyB.m_jointList = joint.m_nodeB;
    if (def.collideConnected === false) {
      const body = def.body1.m_shapeCount < def.body2.m_shapeCount ? def.body1 : def.body2;
      let fixture = body.m_fixtureList;
      while (fixture) {
        fixture.RefilterProxy(this.m_broadPhase, body.m_xf);
        fixture = fixture.m_next;
      }
    }
    return joint;
  }

  DestroyJoint(joint) {
    if (!joint) {
      return;
    }
    const bodyA = joint.m_bodyA;
    const bodyB = joint.m_bodyB;
    const collideConnected = joint.m_collideConnected;
    bodyA.WakeUp();
    bodyB.WakeUp();
    if (joint.m_prev) {
      joint.m_prev.m_next = joint.m_next;
    }
    if (joint.m_next) {
      joint.m_next.m_prev = joint.m_prev;
    }
    if (joint === this.m_jointList) {
      this.m_jointList = joint.m_next;
    }
    if (joint.m_nodeA.prev) {
      joint.m_nodeA.prev.next = joint.m_nodeA.next;
    }
    if (joint.m_nodeA.next) {
      joint.m_nodeA.next.prev = joint.m_nodeA.prev;
    }
    if (joint.m_nodeA === bodyA.m_jointList) {
      bodyA.m_jointList = joint.m_nodeA.next;
    }
    if (joint.m_nodeB.prev) {
      joint.m_nodeB.prev.next = joint.m_nodeB.next;
    }
    if (joint.m_nodeB.next) {
      joint.m_nodeB.next.prev = joint.m_nodeB.prev;
    }
    if (joint.m_nodeB === bodyB.m_jointList) {
      bodyB.m_jointList = joint.m_nodeB.next;
    }
    this.m_jointCount -= 1;
    if (collideConnected === false) {
      const body = bodyA.m_shapeCount < bodyB.m_shapeCount ? bodyA : bodyB;
      let fixture = body.m_fixtureList;
      while (fixture) {
        fixture.RefilterProxy(this.m_broadPhase, body.m_xf);
        fixture = fixture.m_next;
      }
    }
  }

  QueryAABB(callback, aabb) {
    const fixtures = [];
    const count = this.m_broadPhase.QueryAABB(aabb, fixtures, 512);
    for (let index = 0; index < count; index += 1) {
      if (callback(fixtures[index]) === false) {
        break;
      }
    }
  }

  ClearForces() {
    let body = this.m_bodyList;
    while (body) {
      body.m_force.SetZero();
      body.m_torque = 0;
      body = body.m_next;
    }
  }

  Refilter(shapeOrFixture) {
    if (!shapeOrFixture) {
      return;
    }
    if (typeof shapeOrFixture.RefilterProxy === "function") {
      const body = shapeOrFixture.m_body ?? shapeOrFixture.GetBody?.();
      shapeOrFixture.RefilterProxy(this.m_broadPhase, body.m_xf);
    }
  }

  Solve(step) {
    this.m_positionIterationCount = 0;
    const island = new b2Island();
    island.Initialize(this.m_bodyCount, this.m_contactCount, this.m_jointCount, this.m_stackAllocator, this.m_contactListener);
    let body = this.m_bodyList;
    while (body) {
      body.m_flags &= ~b2Body.e_islandFlag;
      body = body.m_next;
    }
    let contact = this.m_contactList;
    while (contact) {
      contact.m_flags &= ~contact.constructor.e_islandFlag;
      contact = contact.m_next;
    }
    let joint = this.m_jointList;
    while (joint) {
      joint.m_islandFlag = false;
      joint = joint.m_next;
    }
    const stack = new Array(this.m_bodyCount);
    let seed = this.m_bodyList;
    while (seed) {
      if ((seed.m_flags & (b2Body.e_islandFlag | b2Body.e_sleepFlag | b2Body.e_frozenFlag)) === 0 && !seed.IsStatic()) {
        island.Clear();
        let stackCount = 0;
        stack[stackCount] = seed;
        stackCount += 1;
        seed.m_flags |= b2Body.e_islandFlag;
        while (stackCount > 0) {
          const bodyNode = stack[stackCount - 1];
          stackCount -= 1;
          island.AddBody(bodyNode);
          bodyNode.m_flags &= ~b2Body.e_sleepFlag;
          if (!bodyNode.IsStatic()) {
            let contactEdge = bodyNode.m_contactList;
            while (contactEdge) {
              const edgeContact = contactEdge.contact;
              if ((edgeContact.m_flags & (edgeContact.constructor.e_islandFlag | edgeContact.constructor.e_nonSolidFlag)) === 0 && edgeContact.m_manifoldCount !== 0) {
                island.AddContact(edgeContact);
                edgeContact.m_flags |= edgeContact.constructor.e_islandFlag;
                const other = contactEdge.other;
                if ((other.m_flags & b2Body.e_islandFlag) === 0) {
                  stack[stackCount] = other;
                  stackCount += 1;
                  other.m_flags |= b2Body.e_islandFlag;
                }
              }
              contactEdge = contactEdge.next;
            }
            let jointEdge = bodyNode.m_jointList;
            while (jointEdge) {
              const edgeJoint = jointEdge.joint;
              if (edgeJoint.m_islandFlag !== true) {
                island.AddJoint(edgeJoint);
                edgeJoint.m_islandFlag = true;
                const other = jointEdge.other;
                if ((other.m_flags & b2Body.e_islandFlag) === 0) {
                  stack[stackCount] = other;
                  stackCount += 1;
                  other.m_flags |= b2Body.e_islandFlag;
                }
              }
              jointEdge = jointEdge.next;
            }
          }
        }
        island.Solve(step, this.m_gravity, this.m_allowSleep);
        if (island.m_positionIterationCount > this.m_positionIterationCount) {
          this.m_positionIterationCount = island.m_positionIterationCount;
        }
        for (let index = 0; index < island.m_bodyCount; index += 1) {
          const islandBody = island.m_bodies[index];
          if (islandBody.IsStatic()) {
            islandBody.m_flags &= ~b2Body.e_islandFlag;
          }
        }
      }
      seed = seed.m_next;
    }
    body = this.m_bodyList;
    while (body) {
      if ((body.m_flags & (b2Body.e_sleepFlag | b2Body.e_frozenFlag)) === 0 && !body.IsStatic()) {
        body.SynchronizeShapes();
      }
      body = body.m_next;
    }
    this.m_broadPhase.Commit();
  }

  SolveTOI(step) {
    const island = new b2Island();
    island.Initialize(this.m_bodyCount, b2Settings.b2_maxTOIContactsPerIsland, 0, this.m_stackAllocator, this.m_contactListener);
    const stack = new Array(this.m_bodyCount);

    let body = this.m_bodyList;
    while (body) {
      body.m_flags &= ~b2Body.e_islandFlag;
      body.m_sweep.t0 = 0;
      body = body.m_next;
    }

    let contact = this.m_contactList;
    while (contact) {
      contact.m_flags &= ~(contact.constructor.e_toiFlag | contact.constructor.e_islandFlag);
      contact = contact.m_next;
    }

    while (true) {
      let minContact = null;
      let minTOI = 1;
      contact = this.m_contactList;
      while (contact) {
        if ((contact.m_flags & (contact.constructor.e_slowFlag | contact.constructor.e_nonSolidFlag)) !== 0) {
          contact = contact.m_next;
          continue;
        }

        let toi = 1;
        if ((contact.m_flags & contact.constructor.e_toiFlag) !== 0) {
          toi = contact.m_toi;
        } else {
          const shapeA = contact.m_shape1;
          const shapeB = contact.m_shape2;
          const bodyA = shapeA.m_body;
          const bodyB = shapeB.m_body;
          if ((bodyA.IsStatic() || bodyA.IsSleeping()) && (bodyB.IsStatic() || bodyB.IsSleeping())) {
            contact = contact.m_next;
            continue;
          }

          let t0 = bodyA.m_sweep.t0;
          if (bodyA.m_sweep.t0 < bodyB.m_sweep.t0) {
            t0 = bodyB.m_sweep.t0;
            bodyA.m_sweep.Advance(t0);
          } else if (bodyB.m_sweep.t0 < bodyA.m_sweep.t0) {
            t0 = bodyA.m_sweep.t0;
            bodyB.m_sweep.Advance(t0);
          }

          toi = contact.ComputeTOI(bodyA.m_sweep, bodyB.m_sweep);
          if (toi > 0 && toi < 1) {
            toi = (1 - toi) * t0 + toi;
            if (toi > 1) {
              toi = 1;
            }
          }
          contact.m_toi = toi;
          contact.m_flags |= contact.constructor.e_toiFlag;
        }

        if (toi > Number.MIN_VALUE && toi < minTOI) {
          minContact = contact;
          minTOI = toi;
        }
        contact = contact.m_next;
      }

      if (!minContact || minTOI > 1 - 100 * Number.MIN_VALUE) {
        break;
      }

      const shapeA = minContact.m_shape1;
      const shapeB = minContact.m_shape2;
      const bodyA = shapeA.m_body;
      const bodyB = shapeB.m_body;
      bodyA.Advance(minTOI);
      bodyB.Advance(minTOI);
      minContact.Update(this.m_contactListener);
      minContact.m_flags &= ~minContact.constructor.e_toiFlag;

      if (minContact.m_manifoldCount === 0) {
        continue;
      }

      let seed = bodyA;
      if (seed.IsStatic()) {
        seed = bodyB;
      }

      island.Clear();
      let stackCount = 0;
      stack[stackCount] = seed;
      stackCount += 1;
      seed.m_flags |= b2Body.e_islandFlag;

      while (stackCount > 0) {
        const bodyNode = stack[stackCount - 1];
        stackCount -= 1;
        island.AddBody(bodyNode);
        bodyNode.m_flags &= ~b2Body.e_sleepFlag;
        if (bodyNode.IsStatic()) {
          continue;
        }

        let contactEdge = bodyNode.m_contactList;
        while (contactEdge) {
          if (island.m_contactCount !== island.m_contactCapacity) {
            const edgeContact = contactEdge.contact;
            if ((edgeContact.m_flags & (edgeContact.constructor.e_islandFlag | edgeContact.constructor.e_slowFlag | edgeContact.constructor.e_nonSolidFlag)) === 0
              && edgeContact.m_manifoldCount !== 0) {
              island.AddContact(edgeContact);
              edgeContact.m_flags |= edgeContact.constructor.e_islandFlag;
              const other = contactEdge.other;
              if ((other.m_flags & b2Body.e_islandFlag) === 0) {
                if (!other.IsStatic()) {
                  other.Advance(minTOI);
                  other.WakeUp();
                }
                stack[stackCount] = other;
                stackCount += 1;
                other.m_flags |= b2Body.e_islandFlag;
              }
            }
          }
          contactEdge = contactEdge.next;
        }
      }

      const subStep = {
        dt: b2Scalar((1 - minTOI) * step.dt),
        inv_dt: 0,
        maxIterations: step.maxIterations,
        velocityIterations: step.velocityIterations,
        positionIterations: step.positionIterations,
        dtRatio: 0,
        positionCorrection: step.positionCorrection,
        warmStarting: false,
      };
      subStep.inv_dt = b2Scalar(1 / subStep.dt);
      island.SolveTOI(subStep);

      for (let index = 0; index < island.m_bodyCount; index += 1) {
        const islandBody = island.m_bodies[index];
        islandBody.m_flags &= ~b2Body.e_islandFlag;
        if ((islandBody.m_flags & (b2Body.e_sleepFlag | b2Body.e_frozenFlag)) !== 0 || islandBody.IsStatic()) {
          continue;
        }
        islandBody.SynchronizeShapes();
        let contactEdge = islandBody.m_contactList;
        while (contactEdge) {
          contactEdge.contact.m_flags &= ~contactEdge.contact.constructor.e_toiFlag;
          contactEdge = contactEdge.next;
        }
      }

      for (let index = 0; index < island.m_contactCount; index += 1) {
        const islandContact = island.m_contacts[index];
        islandContact.m_flags &= ~(islandContact.constructor.e_toiFlag | islandContact.constructor.e_islandFlag);
      }

      this.m_broadPhase.Commit();
    }
  }

  Step(dt = 0, velocityIterations = 0, positionIterations = 0) {
    b2World.s_debugStepCounter += 1;
    if (typeof b2World.s_debugStepObserver === "function") {
      try {
        b2World.s_debugStepObserver({
          kind: "world_step",
          frame: b2World.s_debugStepCounter,
          dt,
          velocityIterations,
          positionIterations,
          world: this,
        });
      } catch (_err) {
        // Debug hooks must never perturb simulation flow.
      }
    }
    this.m_lock = true;
    const step = b2World.s_timestep2;
    const maxIterations = velocityIterations || positionIterations || 0;
    step.dt = b2Scalar(dt);
    step.maxIterations = maxIterations;
    step.velocityIterations = maxIterations;
    step.positionIterations = maxIterations;
    step.inv_dt = dt > 0 ? b2Scalar(1 / dt) : 0;
    step.dtRatio = b2Scalar(this.m_inv_dt0 * dt);
    step.positionCorrection = b2World.m_positionCorrection;
    step.warmStarting = b2World.m_warmStarting;
    // AS3 Step() ordering: Collide → Solve → SolveTOI.
    // No FindNewContacts — new contacts arrive via BroadPhase.Commit() callbacks.
    this.m_contactManager.Collide();
    if (step.dt > 0) {
      this.Solve(step);
      if (b2World.m_continuousPhysics) {
        this.SolveTOI(step);
      }
    }
    this.m_inv_dt0 = b2Scalar(step.inv_dt);
    this.m_lock = false;
  }
}
